// Shared trigger logic for the Feedback Card (APR-1193).
// Thresholds mirror backend ApplicationFeedback constants.
export const FEEDBACK_HIGH_PERSONALITY = 65
export const FEEDBACK_LOW_PERSONALITY = 40
export const FEEDBACK_TRIGGER_TYPE_KEY = 'feedback_trigger_type'

// On a contradictory (bulk) decline/hire, let the status-change toast land first,
// then surface the card a beat later so the two don't stack at once (designer ask).
export const FEEDBACK_CARD_TOAST_DELAY_MS = 800

export function personalityScoreOf(application) {
	return (
		application?.personality_match_score ??
		application?.match?.percents?.personality ??
		null
	)
}

// trigger_type for a contradictory status change, or null when no card is due.
export function feedbackTriggerType(application, status) {
	// Once-per-application guard: backend flags applications this user already
	// gave feedback on; never re-prompt (the DB enforces the same uniqueness).
	if (application?.has_feedback) return null

	const score = personalityScoreOf(application)

	if (score === null) return null

	if (score >= FEEDBACK_HIGH_PERSONALITY && status === 'denied') {
		return 'high_personality_declined'
	}

	if (score < FEEDBACK_LOW_PERSONALITY && status === 'hired') {
		return 'low_personality_hired'
	}

	return null
}

export function withFeedbackTriggerType(application, triggerType) {
	if (!application || !triggerType) {
		return application
	}

	return {
		...application,
		[FEEDBACK_TRIGGER_TYPE_KEY]: triggerType
	}
}

export function feedbackTriggerTypeOf(application) {
	return (
		application?.[FEEDBACK_TRIGGER_TYPE_KEY] ??
		application?.feedbackTriggerType ??
		null
	)
}

// Accumulate a contradictory decision for later (profile-modal review flow):
// appends the application with the exact trigger_type for this status change,
// replacing/removing any earlier entry for the same id as the decision changes.
export function addPendingFeedback(pending, application, status) {
	if (!application) {
		return pending
	}

	const triggerType = feedbackTriggerType(application, status)
	const withoutApplication = pending.filter(app => app.id != application.id)

	if (!triggerType) {
		return withoutApplication.length === pending.length
			? pending
			: withoutApplication
	}

	return [
		...withoutApplication,
		withFeedbackTriggerType(application, triggerType)
	]
}

export function queuePendingFeedback(pending, application, status) {
	const previousPendingIndex = application
		? pending.findIndex(app => app.id == application.id)
		: -1
	const previousPendingApplication =
		previousPendingIndex >= 0 ? pending[previousPendingIndex] : null
	const nextPending = addPendingFeedback(pending, application, status)

	if (nextPending === pending) {
		return {
			pending: nextPending,
			queuedFeedback: null
		}
	}

	return {
		pending: nextPending,
		queuedFeedback: {
			applicationId: application.id,
			previousPendingApplication,
			previousPendingIndex
		}
	}
}

export function rollbackPendingFeedback(pending, queuedFeedback) {
	if (!queuedFeedback) {
		return pending
	}

	const withoutFailedApplication = pending.filter(
		app => app.id != queuedFeedback.applicationId
	)

	if (!queuedFeedback.previousPendingApplication) {
		return withoutFailedApplication
	}

	const restoreIndex =
		queuedFeedback.previousPendingIndex >= 0
			? Math.min(
					queuedFeedback.previousPendingIndex,
					withoutFailedApplication.length
			  )
			: withoutFailedApplication.length

	return [
		...withoutFailedApplication.slice(0, restoreIndex),
		queuedFeedback.previousPendingApplication,
		...withoutFailedApplication.slice(restoreIndex)
	]
}
